import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import apiClient from '../../../utils/apiclient'
import { useNavigate } from 'react-router-dom'
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap, useMapEvents } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix Leaflet's default icon issue with bundlers (same pattern as AllRiders.jsx)
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconAnchor: [12, 41]
})
L.Marker.prototype.options.icon = DefaultIcon

const TIME_SLOTS = [
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00',
]

const ORDER_STATUSES = [
  'ordered', 'delivered', 'cancelled', 'on the way', 'Refunded', 'Rider is assigned',
  'payment_cancelled', 'delivered_Refunded', 'payment_initiated',
]

const RESTAURANTS_URL = 'https://ppod.getbentobox.in/admin/restaurant'
const getDateString = (date) => date.toISOString().slice(0, 10)

// Fit all order pins on one screen when the map opens
const OrdersMapFit = ({ points, fitKey }) => {
  const map = useMap()
  const fittedKey = useRef(null)
  useEffect(() => {
    // Allow the modal layout to settle before measuring
    const timer = setTimeout(() => {
      map.invalidateSize()
      // Only auto-fit when the data changes — never fight user zoom/pan
      if (fittedKey.current === fitKey) return
      fittedKey.current = fitKey
      if (!points || points.length === 0) return
      if (points.length === 1) {
        map.setView(points[0], 15)
      } else {
        map.fitBounds(L.latLngBounds(points), { padding: [50, 50], maxZoom: 16 })
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [map, points, fitKey])
  return null
}

// Track zoom so spider connector dots/lines only render when zoomed in
const MapZoomTracker = ({ onZoom }) => {
  const map = useMapEvents({
    zoomend: () => onZoom(map.getZoom()),
  })
  useEffect(() => { onZoom(map.getZoom()) }, [map, onZoom])
  return null
}

// Clicking a cluster smoothly flies to its bounds, then pins split naturally.
// (Default zoomToBoundsOnClick is off so it doesn't fight this animation.)
const ClusterClickFly = ({ clusterRef }) => {
  const map = useMap()
  useEffect(() => {
    let detach = null
    const attach = () => {
      const group = clusterRef.current
      if (!group) return false
      const handler = (e) => {
        const cluster = e.layer
        try {
          if (map.getZoom() >= (map.getMaxZoom() || 18) - 0.01) {
            cluster.spiderfy()
            return
          }
          map.flyToBounds(cluster.getBounds().pad(0.6), { duration: 0.9, maxZoom: 17 })
        } catch {
          try { cluster.zoomToBounds() } catch { /* noop */ }
        }
      }
      group.on('clusterclick', handler)
      detach = () => group.off('clusterclick', handler)
      return true
    }
    if (!attach()) {
      const timer = setTimeout(attach, 400)
      return () => { clearTimeout(timer); detach && detach() }
    }
    return () => { detach && detach() }
  }, [map, clusterRef])
  return null
}

// Group orders sharing (near-)identical coordinates so stacked pins show as one marker with count
const groupOrdersByLocation = (orders) => {
  const groups = new Map()
  orders.forEach((order) => {
    const lat = parseFloat(order.latitude ?? order.lat)
    const lng = parseFloat(order.longitude ?? order.lng ?? order.long)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
    const key = `${lat.toFixed(6)},${lng.toFixed(6)}`
    if (!groups.has(key)) groups.set(key, { latitude: lat, longitude: lng, orders: [] })
    groups.get(key).orders.push(order)
  })
  return [...groups.values()]
}

// Status pill colors for map popups
const statusStyle = (status) => {
  const s = String(status || '').toLowerCase()
  if (s.includes('deliver') && !s.includes('refund')) return 'bg-green-100 text-green-700'
  if (s.includes('cancel') || s.includes('refund')) return 'bg-red-100 text-red-700'
  if (s.includes('on the way') || s.includes('rider') || s.includes('assign')) return 'bg-blue-100 text-blue-700'
  if (s.includes('payment')) return 'bg-purple-100 text-purple-700'
  return 'bg-amber-100 text-amber-700'
}

// Marker icon with the order count written on it - classic pin: round top, pointy bottom
// iconAnchor is set to the pin tip so the pointy part sits exactly on the order lat/lng
// Icons are cached per count so re-renders (e.g. on zoom) reuse the same instance
// instead of forcing every marker to refresh its icon (which flashes clusters).
const countIconCache = new Map()
const orderCountIcon = (count) => {
  if (!countIconCache.has(count)) {
    countIconCache.set(count, L.divIcon({
      className: 'orders-count-marker-wrap',
      html: `<div class="orders-count-marker"><span>${count}</span></div>`,
      iconSize: [36, 44],
      iconAnchor: [18, 43],
      popupAnchor: [0, -43],
    }))
  }
  return countIconCache.get(count)
}

// --- Always-on spiderfy: spread nearby pins so none hide behind others ---
// True lat/lng is kept (dot + connector line), display position is fanned out.
const SPIDER_CLUSTER_RADIUS_M = 60
const toRad = (deg) => (deg * Math.PI) / 180
const haversineM = (lat1, lng1, lat2, lng2) => {
  const R = 6371000
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2)
  return 2 * R * Math.asin(Math.sqrt(a))
}

const clusterGroupsByProximity = (groups, radiusM) => {
  const clusters = []
  groups.forEach((group) => {
    let placed = false
    for (const cluster of clusters) {
      if (cluster.some((member) => haversineM(group.latitude, group.longitude, member.latitude, member.longitude) < radiusM)) {
        cluster.push(group)
        placed = true
        break
      }
    }
    if (!placed) clusters.push([group])
  })
  return clusters
}

const buildSpiderLayout = (groups) => {
  const clusters = clusterGroupsByProximity(groups, SPIDER_CLUSTER_RADIUS_M)
  const laidOut = []
  clusters.forEach((cluster) => {
    if (cluster.length === 1) {
      const g = cluster[0]
      laidOut.push({ ...g, displayLatitude: g.latitude, displayLongitude: g.longitude, spiderfied: false })
      return
    }
    const centroidLat = cluster.reduce((sum, g) => sum + g.latitude, 0) / cluster.length
    const centroidLng = cluster.reduce((sum, g) => sum + g.longitude, 0) / cluster.length
    const spreadM = Math.min(90, 32 + cluster.length * 6)
    cluster.forEach((g, i) => {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / cluster.length
      const dLat = (spreadM * Math.cos(angle)) / 111320
      const dLng = (spreadM * Math.sin(angle)) / (111320 * Math.cos(toRad(centroidLat)) || 1)
      laidOut.push({
        ...g,
        displayLatitude: centroidLat + dLat,
        displayLongitude: centroidLng + dLng,
        spiderfied: true,
      })
    })
  })
  return laidOut
}

const MultiSelectDropdown = ({ title, options, selectedValues, onToggle, onSetValues }) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)
  const allSelected = options.length > 0 && selectedValues.length === options.length
  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.value))
    .map((option) => option.label)
  const buttonLabel = selectedLabels.length === 0
    ? `Select ${title.toLowerCase()}`
    : selectedLabels.length === 1
      ? selectedLabels[0]
      : `${selectedLabels.length} selected`

  useEffect(() => {
    if (!isOpen) return
    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false)
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    return () => document.removeEventListener('mousedown', closeOnOutsideClick)
  }, [isOpen])

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        type="button"
        className={`w-full min-h-10 border-2 rounded-lg px-3 py-2 text-sm text-left flex items-center justify-between gap-2 focus:outline-none focus:border-blue-500 ${selectedValues.length > 0 ? 'border-green-500' : 'border-gray-200'}`}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-label={title}
      >
        <span className="min-w-0 truncate">
          <span className="text-xs font-semibold text-gray-500">{title}: </span>
          <span>{buttonLabel}</span>
        </span>
        <span aria-hidden="true" className="text-gray-500">{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && (
        <div className="absolute left-0 top-full z-30 mt-1 w-full min-w-56 rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
            <span className="text-xs text-gray-500">{selectedValues.length} selected</span>
            <div className="flex gap-3">
              <button type="button" className="text-xs font-medium text-blue-700 hover:underline" onClick={() => onSetValues(allSelected ? [] : options.map((option) => option.value))}>
                {allSelected ? 'Clear' : 'Select all'}
              </button>
              <button type="button" className="text-xs font-medium text-gray-600 hover:underline" onClick={() => setIsOpen(false)}>Done</button>
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto p-2">
            {options.map((option) => (
              <label key={option.value} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                <input type="checkbox" checked={selectedValues.includes(option.value)} onChange={() => onToggle(option.value)} />
                <span className="truncate">{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const GetOrders = ({ route }) => {
  const navigate = useNavigate()
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const [filters, setFilters] = useState({
    restaurantIds: [36],
    timeslot: [],
    orderStatus: ['ordered'],
    startDate: '',
    endDate: '',
  })
  const [restaurants, setRestaurants] = useState([])
  const [loadingRestaurants, setLoadingRestaurants] = useState(false)
  const [orders, setOrders] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showMap, setShowMap] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [mapZoom, setMapZoom] = useState(5)
  const handleZoom = useCallback((z) => setMapZoom(z), [])
  const clusterRef = useRef(null)
  const mapRef = useRef(null)
  const mapHistoryPushed = useRef(false)

  // Push a history entry while the map is open so the phone back button closes the map first
  useEffect(() => {
    if (!showMap) return
    window.history.pushState({ ordersMap: true }, '')
    mapHistoryPushed.current = true
    const onPopState = () => {
      mapHistoryPushed.current = false
      setShowMap(false)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [showMap])

  const closeMap = () => {
    // Go back through the entry we pushed so history stays clean;
    // the popstate handler above actually closes the modal
    if (mapHistoryPushed.current) {
      mapHistoryPushed.current = false
      window.history.back()
      // Fallback in case popstate doesn't fire (e.g. no history entry)
      setTimeout(() => setShowMap(false), 50)
    } else {
      setShowMap(false)
    }
  }

  useEffect(() => {
    fetchRestaurants()
    if (filters.restaurantIds.length > 0 && filters.orderStatus.length > 0) {
      fetchOrders()
    }
  }, [route.route])

  const fetchRestaurants = async () => {
    try {
      setLoadingRestaurants(true)
      const response = await apiClient.get("/restaurant")
      const restaurantList = Array.isArray(response.data) ? response.data : response.data?.content || []
      setRestaurants(restaurantList)
    } catch (requestError) {
      console.error('Failed to fetch restaurants', requestError)
      setRestaurants([])
      setError('Unable to fetch restaurants. Please try again.')
    } finally {
      setLoadingRestaurants(false)
    }
  }

  const fetchOrders = async () => {
    const restaurantId = filters.restaurantIds
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value > 0)

    if (restaurantId.length === 0 || filters.orderStatus.length === 0) {
      setError('Add at least one restaurant and order status.')
      return
    }

    try {
      setLoading(true)
      setError('')
      const requestBody = {
        restaurantId,
        timeslot: filters.timeslot.length > 0 ? filters.timeslot : TIME_SLOTS,
        orderStatus: filters.orderStatus,
        startDate: filters.startDate || '2000-01-01',
        endDate: filters.endDate || '2100-01-01',
      }
      const response = await apiClient.post('/OrderByAddresDashboard', requestBody)
      const responseData = response.data || {}
      const subscriptionOrders = (responseData.subscriptionOrders || []).map((order) => ({ ...order, type: 'Subscription' }))
      const oneTimeOrders = (responseData.orders || responseData.oneTimeOrders || []).map((order) => ({ ...order, type: 'One Time' }))
      setOrders([...subscriptionOrders, ...oneTimeOrders])
    } catch (requestError) {
      console.error('Failed to fetch orders', requestError)
      setOrders([])
      setError('Unable to fetch orders. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const updateFilter = (field, value) => {
    setFilters((previous) => ({ ...previous, [field]: value }))
  }

  const toggleFilterValue = (field, value) => {
    setFilters((previous) => ({
      ...previous,
      [field]: previous[field].includes(value)
        ? previous[field].filter((selected) => selected !== value)
        : [...previous[field], value],
    }))
  }

  const selectedRestaurants = filters.restaurantIds
    .map((id) => restaurants.find((restaurant) => Number(restaurant.id) === Number(id)))
    .filter(Boolean)
  const selectedRestaurantName = restaurants.length > 0 && filters.restaurantIds.length === restaurants.length
    ? 'All restaurants'
    : selectedRestaurants.length === 1
      ? selectedRestaurants[0].name || selectedRestaurants[0].restaurantName || 'Restaurant'
      : selectedRestaurants.length > 1
        ? `${selectedRestaurants.length} restaurants`
        : 'No restaurants'
  const selectedTimeslots = filters.timeslot.length === 0
    ? 'Any slot'
    : filters.timeslot.length === 1
      ? filters.timeslot[0]
      : `${filters.timeslot.length} time slots`
  const selectedStatuses = filters.orderStatus.length === 0
    ? 'Any status'
    : filters.orderStatus.length === 1
      ? filters.orderStatus[0]
      : `${filters.orderStatus.length} statuses`
  const filterSummary = [selectedRestaurantName, selectedTimeslots, selectedStatuses].join(' • ')

  const filteredOrders = useMemo(() => orders.filter((order) => {
    const term = search.trim().toLowerCase();
    if (!term) return true
    return [order.id, order.publicId, order.number, order.userdetails?.name, order.restaurantName, order.mealName, order.status, order.orderStatus]
      .some((value) => String(value ?? '').toLowerCase().includes(term))
  }), [orders, search])

  const mappableGroups = useMemo(() => groupOrdersByLocation(filteredOrders), [filteredOrders])
  const spiderGroups = useMemo(() => buildSpiderLayout(mappableGroups), [mappableGroups])
  const mappableCount = mappableGroups.reduce((sum, group) => sum + group.orders.length, 0)
  const mapPoints = useMemo(() => spiderGroups.flatMap((group) => [
    [group.displayLatitude, group.displayLongitude],
    [group.latitude, group.longitude],
  ]), [spiderGroups])
  const mapFitKey = useMemo(
    () => `${mappableGroups.length}:${mappableCount}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mappableGroups.length, mappableCount]
  )
  // Cluster icon shows total ORDERS inside (not just pins) by matching child latlngs
  const clusterIcon = useCallback((cluster) => {
    let totalOrders = 0
    try {
      cluster.getAllChildMarkers().forEach((marker) => {
        const ll = marker.getLatLng()
        const match = spiderGroups.find(
          (g) => Math.abs(g.displayLatitude - ll.lat) < 1e-7 && Math.abs(g.displayLongitude - ll.lng) < 1e-7
        )
        totalOrders += match ? match.orders.length : 1
      })
    } catch {
      totalOrders = cluster.getChildCount()
    }
    if (!totalOrders) totalOrders = cluster.getChildCount()
    return L.divIcon({
      html: `<div class="orders-cluster-pin"><span>${totalOrders}</span></div>`,
      className: 'orders-cluster-wrap',
      iconSize: L.point(44, 52, true),
      iconAnchor: [22, 51],
    })
  }, [spiderGroups])
  // Keep popups inside small phone screens (map width ≈ viewport - modal padding)
  const popupMaxWidth = typeof window !== 'undefined' && window.innerWidth < 768
    ? Math.max(240, window.innerWidth - 32)
    : 380

  return (
    <div className="w-full flex-1 flex flex-col min-h-0 pb-4">
      <div className="flex flex-col gap-2 md:gap-3 mb-3 md:mb-4 flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-base md:text-xl font-semibold text-gray-800">Get Orders</h2>
            <p className="hidden md:block text-sm text-gray-500 mt-1">Fetch subscription and one-time orders by restaurant, time slot, status, and date.</p>
            {!showFilters && (
              <p className="md:hidden text-xs text-gray-500 mt-0.5 truncate">
                {filterSummary}
              </p>
            )}
          </div>
          <button
            onClick={() => setShowFilters((prev) => !prev)}
            className="md:hidden flex-shrink-0 border-2 border-gray-200 rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700 hover:border-blue-500 transition-colors"
          >
            {showFilters ? 'Hide ▲' : 'Filters ▼'}
          </button>
        </div>
        <div className={`${showFilters ? 'grid' : 'hidden'} md:grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2`}>
          <MultiSelectDropdown
            title="Restaurants"
            options={restaurants.map((restaurant) => ({
              value: Number(restaurant.id),
              label: restaurant.name || restaurant.restaurantName || `Restaurant ${restaurant.id}`,
            }))}
            selectedValues={filters.restaurantIds}
            onToggle={(value) => toggleFilterValue('restaurantIds', value)}
            onSetValues={(values) => updateFilter('restaurantIds', values)}
          />
          <MultiSelectDropdown
            title="Time slots"
            options={TIME_SLOTS.map((slot) => ({ value: slot, label: slot }))}
            selectedValues={filters.timeslot}
            onToggle={(value) => toggleFilterValue('timeslot', value)}
            onSetValues={(values) => updateFilter('timeslot', values)}
          />
          <MultiSelectDropdown
            title="Order statuses"
            options={ORDER_STATUSES.map((status) => ({ value: status, label: status }))}
            selectedValues={filters.orderStatus}
            onToggle={(value) => toggleFilterValue('orderStatus', value)}
            onSetValues={(values) => updateFilter('orderStatus', values)}
          />
          <input type="date" className={`border-2 py-2 px-3 text-sm rounded-lg outline-none focus:border-blue-500 ${filters.startDate ? 'border-green-500' : 'border-gray-200'}`} value={filters.startDate} onChange={(event) => updateFilter('startDate', event.target.value)} aria-label="Start date" />
          <div className="flex gap-2">
            <input type="date" className={`border-2 py-2 px-3 text-sm rounded-lg outline-none focus:border-blue-500 min-w-0 flex-1 ${filters.endDate ? 'border-green-500' : 'border-gray-200'}`} value={filters.endDate} onChange={(event) => updateFilter('endDate', event.target.value)} aria-label="End date" />
            <button onClick={fetchOrders} className="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-blue-700 transition-colors">Load</button>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center gap-2 md:gap-3 mb-2 md:mb-3 flex-wrap flex-shrink-0">
        <span className="text-sm text-gray-600">Showing {filteredOrders.length} of {orders.length} orders</span>
        <div className="flex gap-2 items-center w-full md:w-auto">
          <input className="border-2 py-2 px-3 text-sm rounded-lg outline-none focus:border-blue-500 w-full md:w-80" placeholder="Search orders" value={search} onChange={(event) => setSearch(event.target.value)} />
          <button
            onClick={() => setShowMap(true)}
            disabled={mappableGroups.length === 0}
            title={mappableGroups.length === 0 ? 'No order locations to show' : `Show ${mappableCount} orders on map`}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors whitespace-nowrap ${mappableGroups.length === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
          >
            Map ({mappableCount})
          </button>
        </div>
      </div>

      {error && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex-shrink-0">{error}</div>}
      {loading && <div className="text-center py-4 text-gray-500 flex-shrink-0">Loading orders...</div>}
      <div className="flex-1 min-h-0 overflow-auto border border-gray-200 rounded-lg">
        <table className="min-w-[1500px] w-full bg-white border-collapse">
          <thead className="bg-gray-800 text-white sticky top-0 z-10">
            <tr>{['ID', 'Public ID', 'Customer', 'Restaurant', 'Status', 'Type', 'Meal', 'Address', 'Order Date', 'Time', 'OTP', 'Rider', 'Created At'].map((heading) => <th key={heading} className="px-3 py-3 text-left text-xs">{heading}</th>)}</tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 && !loading ? <tr><td colSpan="13" className="text-center py-8 text-gray-500">No orders found for the selected filters.</td></tr> : filteredOrders.map((order, index) => (
              <tr key={order.id || order.publicId || index} className="border-t border-gray-200">
                <td className="px-3 py-2 text-xs">{order.id ?? '-'}</td>
                <td className="px-3 py-2 text-xs">{order.publicId ?? '-'}</td>
                <td className="px-3 py-2 text-xs">{order.userdetails?.name || order.customerName || order.number || '-'}</td>
                <td className="px-3 py-2 text-xs hover:text-blue-900 hover:underline cursor-pointer" onClick={() => navigate(`/restaurant/${order.restaurantid || order.restaurantId || ''}`)}>{order.restaurantName || '-'}</td>
                <td className="px-3 py-2 text-xs">{order.status || order.orderStatus || '-'}</td>
                <td className="px-3 py-2 text-xs">{order.type}</td>
                <td className="px-3 py-2 text-xs">{order.mealName || order.mealname || order.description || '-'}</td>
                <td className="px-3 py-2 text-xs">{[order.street, order.city, order.state, order.postalCode].filter(Boolean).join(', ') || '-'}</td>
                <td className="px-3 py-2 text-xs">{order.orderdate || order.orderDate || '-'}</td>
                <td className="px-3 py-2 text-xs">{order.startTime || '-'} - {order.endTime || '-'}</td>
                <td className="px-3 py-2 text-xs">{order.otp || '-'}</td>
                <td className="px-3 py-2 text-xs">{order.riderDetails?.name || order.riderName || '-'}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{order.createdAt ? order.createdAt.replace('T', ' ').split('.')[0] : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showMap && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-stretch md:items-center justify-center z-[9999] md:p-4" onClick={closeMap}>
          <style>{`
            .orders-map .leaflet-popup-content-wrapper { border-radius: 14px; box-shadow: 0 12px 32px rgba(0,0,0,0.25); }
            .orders-map .leaflet-popup-content { margin: 0; line-height: 1.45; }
            .orders-map .leaflet-popup-tip-container { margin-top: -1px; }
            .orders-map-popup-list::-webkit-scrollbar { width: 6px; }
            .orders-map-popup-list::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
            .orders-count-marker-wrap { background: transparent; border: none; padding: 0; }
            .orders-count-marker {
              box-sizing: border-box;
              width: 36px; height: 36px; margin: 0; padding: 0;
              background: #16a34a; color: #fff;
              border: 3px solid #fff;
              border-radius: 50% 50% 50% 0;
              transform: rotate(-45deg);
              transform-origin: center;
              display: flex; align-items: center; justify-content: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.35);
            }
            .orders-count-marker span {
              transform: rotate(45deg);
              font-size: 14px; font-weight: 800; line-height: 1;
            }
            .orders-cluster-wrap { background: transparent; border: none; padding: 0; }
            .orders-cluster-pin {
              box-sizing: border-box;
              width: 42px; height: 42px; margin: 0; padding: 0;
              background: #2563eb; color: #fff;
              border: 3px solid #fff;
              border-radius: 50% 50% 50% 0;
              transform: rotate(-45deg);
              transform-origin: center;
              display: flex; align-items: center; justify-content: center;
              box-shadow: 0 2px 10px rgba(0,0,0,0.4);
            }
            .orders-cluster-pin span {
              transform: rotate(45deg);
              font-size: 15px; font-weight: 800; line-height: 1;
            }
          `}</style>
          <div className="bg-white w-full h-full md:h-auto md:rounded-2xl shadow-2xl md:max-w-6xl overflow-hidden flex flex-col md:max-h-[94vh]" onClick={(event) => event.stopPropagation()}>
            <div className="flex justify-between items-center pl-4 pr-2 py-2.5 border-b border-gray-200 shrink-0 bg-white">
              <div className="min-w-0 pr-2 flex items-center gap-2">
                <span className="bg-green-600 text-white text-xs font-bold rounded-full px-2.5 py-1 flex-shrink-0">{mappableCount}</span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-800 text-sm md:text-base truncate">Orders on Map • {mappableGroups.length} {mappableGroups.length === 1 ? 'location' : 'locations'}</h3>
                  <p className="text-[11px] md:text-xs text-gray-500 truncate">Tap a pin to see orders there</p>
                </div>
              </div>
              <button
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition flex-shrink-0"
                onClick={closeMap}
                title="Close map"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 md:flex-none min-h-0 w-full md:h-[78vh] relative">
              <MapContainer ref={mapRef} center={[20.5937, 78.9629]} zoom={5} zoomControl={false} style={{ height: '100%', width: '100%', minHeight: '100%' }} className="orders-map" zoomAnimation fadeAnimation markerZoomAnimation zoomSnap={0.5} zoomDelta={0.5}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <OrdersMapFit points={mapPoints} fitKey={mapFitKey} />
                <MapZoomTracker onZoom={handleZoom} />
                {mapZoom >= 15 && spiderGroups.filter((g) => g.spiderfied).map((group) => (
                  <React.Fragment key={`line-${group.latitude},${group.longitude}`}>
                    <CircleMarker
                      center={[group.latitude, group.longitude]}
                      radius={4}
                      pathOptions={{ color: '#000', weight: 2, fillColor: '#000', fillOpacity: 1 }}
                    />
                    <Polyline
                      positions={[[group.latitude, group.longitude], [group.displayLatitude, group.displayLongitude]]}
                      pathOptions={{ color: '#6b7280', weight: 1.5, dashArray: '4 4' }}
                    />
                  </React.Fragment>
                ))}
                <MarkerClusterGroup
                  ref={clusterRef}
                  chunkedLoading
                  showCoverageOnHover={false}
                  zoomToBoundsOnClick={false}
                  spiderfyOnMaxZoom
                  animate
                  spiderfyDistanceMultiplier={1.2}
                  disableClusteringAtZoom={17}
                  maxClusterRadius={60}
                  iconCreateFunction={clusterIcon}
                >
                {spiderGroups.map((group) => (
                  <Marker key={`${group.latitude},${group.longitude}`} position={[group.displayLatitude, group.displayLongitude]} icon={orderCountIcon(group.orders.length)}>
                    <Popup
                      maxWidth={popupMaxWidth}
                      minWidth={240}
                      keepInView
                      autoPan
                      autoPanPadding={[24, 24]}
                      closeButton
                    >
                      <div className="w-full">
                        <div className="flex items-center gap-2 px-3.5 pt-3 pb-2.5 border-b border-gray-100">
                          <span className="bg-green-600 text-white text-[11px] font-bold rounded-full min-w-[22px] h-[22px] inline-flex items-center justify-center px-1.5 flex-shrink-0">{group.orders.length}</span>
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-800 text-[13px] truncate">
                              {group.orders.length > 1 ? `${group.orders.length} orders here` : `Order #${group.orders[0].id ?? group.orders[0].publicId ?? ''}`}
                            </div>
                            <div className="text-[11px] text-gray-400 truncate">{group.latitude.toFixed(5)}, {group.longitude.toFixed(5)}</div>
                          </div>
                        </div>
                        <div className="orders-map-popup-list px-2.5 py-2 space-y-2 max-h-[38vh] md:max-h-[46vh] overflow-y-auto overscroll-contain">
                          {group.orders.slice(0, 20).map((order, idx) => {
                            const orderStatus = order.status || order.orderStatus || '-'
                            const timeSlot = [order.startTime, order.endTime].filter(Boolean).join(' - ')
                              || order.timeslot || order.timeSlot || order.slot || '-'
                            return (
                              <div key={order.id || order.publicId || idx} className="bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-semibold text-gray-800 text-[13px]">#{order.id ?? '-'}</span>
                                  <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${statusStyle(orderStatus)}`}>{orderStatus}</span>
                                </div>
                                <div className="text-[11px] text-gray-400 truncate">Public ID: {order.publicId ?? '-'}</div>
                                <div className="text-xs text-gray-600 mt-1 truncate">
                                  {[order.userdetails?.name, order.type].filter(Boolean).join(' • ')}
                                </div>
                                <div className="text-xs text-gray-500 truncate">{order.restaurantName || '-'}</div>
                                <div className="text-xs text-gray-600 mt-1 truncate">🕒 {timeSlot}</div>
                                <div className="text-[11px] text-gray-400 mt-1 leading-snug">
                                  {[order.street, order.city, order.postalCode].filter(Boolean).join(', ') || '-'}
                                </div>
                              </div>
                            )
                          })}
                          {group.orders.length > 20 && <div className="text-xs text-gray-400 text-center pb-1">+{group.orders.length - 20} more…</div>}
                        </div>
                      </div>
                      </Popup>
                  </Marker>
                ))}
                </MarkerClusterGroup>
                <ClusterClickFly clusterRef={clusterRef} />
              </MapContainer>
              <button
                onClick={() => {
                  const map = mapRef.current
                  if (!map || mapPoints.length === 0) return
                  if (mapPoints.length <= 2) {
                    map.flyTo(mapPoints[0], 15, { duration: 0.8 })
                  } else {
                    map.flyToBounds(L.latLngBounds(mapPoints), { padding: [50, 50], maxZoom: 16, duration: 0.8 })
                  }
                }}
                title="Reset to initial view"
                aria-label="Reset to initial view"
                className="absolute bottom-10 right-3 z-[1000] w-11 h-11 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center text-gray-700 hover:text-green-700 hover:border-green-500 active:scale-95 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
                  <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GetOrders